import type { ColumnEditorRequest } from 'app/types/column-editor'

/**
 * Lets a list's Settings open the table's own column editor, which needs the live table
 * instance only the table has. The request names the page whose table should answer: a table
 * still mounted on the page being left must not take it, and one that has not mounted yet
 * (the route or view is about to switch) waits for it, but not forever: a table that never
 * mounts must not pop the editor later on some other page.
 */
export const COLUMN_EDITOR_REQUEST_TTL_MS = 5_000

const listeners = new Set<{ pathname: string; open: (request: ColumnEditorRequest) => void }>()
let pending: { pathname: string; at: number; request: ColumnEditorRequest } | undefined

export function requestColumnEditor(pathname: string, request: ColumnEditorRequest = {}): void {
  pending = { pathname, at: Date.now(), request }
  const answering = [...listeners].filter((listener) => listener.pathname === pathname)
  if (answering.length === 0) return
  pending = undefined
  for (const listener of answering) listener.open(request)
}

export function onColumnEditorRequest(pathname: string, open: (request: ColumnEditorRequest) => void): () => void {
  const listener = { pathname, open }
  listeners.add(listener)
  if (pending && Date.now() - pending.at > COLUMN_EDITOR_REQUEST_TTL_MS) pending = undefined
  if (pending?.pathname === pathname) {
    const { request } = pending
    pending = undefined
    open(request)
  }
  return () => {
    listeners.delete(listener)
  }
}
